import { useRef, useState } from "react";

import type { ContextMenuSubmenuItem as ContextMenuSubmenuItemType } from "../types/contextMenu.types";
import {
    getVisibleMenuItems,
    hasVisibleSubmenuItems,
    isMenuItemDisabled,
} from "../helpers/contextMenu.helpers";
import { getSubmenuDirection } from "../layout/contextMenu.layout";
import { ContextMenuList } from "./ContextMenuList";

type ContextMenuSubmenuItemProps<TContext> = {
    item: ContextMenuSubmenuItemType<TContext>;
    context: TContext;
    closeMenu: () => void;
};

export function ContextMenuSubmenuItem<TContext>({
    item,
    context,
    closeMenu,
}: ContextMenuSubmenuItemProps<TContext>) {
    const [isOpen, setIsOpen] = useState(false);
    const [direction, setDirection] = useState<"right" | "left">("right");
    const itemRef = useRef<HTMLDivElement | null>(null);

    const disabled =
        isMenuItemDisabled(item, context) || !hasVisibleSubmenuItems(item, context);

    const visibleItems = getVisibleMenuItems(item.items, context);

    const openSubmenu = () => {
        if (disabled) {
            return;
        }

        if (itemRef.current) {
            setDirection(
                getSubmenuDirection({
                    parentRect: itemRef.current.getBoundingClientRect(),
                }),
            );
        }

        setIsOpen(true);
    };

    return (
        <div
            ref={itemRef}
            role="none"
            onMouseEnter={openSubmenu}
            onMouseLeave={() => setIsOpen(false)}
            style={{
                position: "relative",
            }}
        >
            <button
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                aria-expanded={isOpen}
                disabled={disabled}
                onClick={openSubmenu}
                style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    border: 0,
                    background: "transparent",
                    padding: "8px 10px",
                    borderRadius: 6,
                    cursor: disabled ? "not-allowed" : "default",
                    opacity: disabled ? 0.5 : 1,
                    color: item.danger ? "#c62828" : "inherit",
                    textAlign: "left",
                }}
            >
                {item.icon ? <span aria-hidden>{item.icon}</span> : null}

                <span style={{ flex: 1 }}>{item.label}</span>

                {item.shortcut ? (
                    <kbd
                        style={{
                            fontSize: 11,
                            opacity: 0.6,
                        }}
                    >
                        {item.shortcut}
                    </kbd>
                ) : null}

                <span aria-hidden>{direction === "right" ? "›" : "‹"}</span>
            </button>

            {isOpen ? (
                <div
                    role="presentation"
                    style={{
                        position: "absolute",
                        top: -6,
                        left: direction === "right" ? "100%" : "auto",
                        right: direction === "left" ? "100%" : "auto",
                        paddingLeft: direction === "right" ? 6 : 0,
                        paddingRight: direction === "left" ? 6 : 0,
                    }}
                >
                    <ContextMenuList
                        items={visibleItems}
                        context={context}
                        closeMenu={closeMenu}
                    />
                </div>
            ) : null}
        </div>
    );
}